import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { getLitigationClients, getLitigationBranches, getLitigationCaseTypes } from "../../../API/litigationApis";

// Helper to format API slug to readable text (e.g., 'civil_suit' -> 'Civil Suit')
const formatCaseTypeLabel = (slug) => {
    if (!slug) return '';
    return slug.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

function CaseListingFilter({ show, close, prevFilter, setPrevFilter, searchByPrev, setSearchByPrev }) {
    const [clientOptions, setClientOptions] = useState([]);
    // branchOptions now holds the *full* list of branches for the selected client
    const [branchOptions, setBranchOptions] = useState([]); 
    
    // State for managing the searchable branch input
    const [branchSearch, setBranchSearch] = useState("");
    // State for showing filtered results for branches
    const [filteredBranchOptions, setFilteredBranchOptions] = useState([]);

    const [caseTypeOptions, setCaseTypeOptions] = useState([]); 

    const [borrowerName, setBorrowerName] = useState(prevFilter?.borrower_name || "");
    const [selectedClient, setSelectedClient] = useState(prevFilter?.client || "");
    const [selectedBranch, setSelectedBranch] = useState(prevFilter?.branch || "");
    const [selectedCaseType, setSelectedCaseType] = useState(prevFilter?.relief_sought || "");
    
    const [selectedDocumentType, setSelectedDocumentType] = useState(prevFilter?.document_type || "");
    
    const [selectedFilingStatus, setSelectedFilingStatus] = useState(prevFilter?.case_status || "");
    const [emailReplyStatus, setEmailReplyStatus] = useState(prevFilter?.email_reply_status || ""); 
    const [caseDataStatus, setCaseDataStatus] = useState(prevFilter?.case_data_status || ""); // Case Data status

    // Hardcoded filing status options
    const filingStatusOptions = [
        { label: "Filed", value: "Filed" },
        { label: "Not Filed", value: "Not Filed" },
    ];

    const caseDataStatusOptions = [
        { label: "Pending", value: "True" },
        { label: "Complete", value: "False" },
    ];
    
    //Hardcoded Document Type options (based on user request values)
    const documentTypeOptions = [
        { label: "Summary Suit", value: "summary_suit" },
        { label: "Commercial Suit", value: "comm_suit" },
        { label: "Execution Petition", value: "pre_ep" },
        { label: "Mediation Application", value: "mediation_application" },
        { label: "Interim Application", value: "interim_application" },
        { label: "Loan Recall Notice", value: "loan_recall_notice" },
        { label: "Original Application", value: "original_application" },
        { label: "Sec 95", value: "sec_95" }
    ];

    // Function to fetch dynamic case types (relief_sought values)
    const fetchCaseTypes = async () => {
        try {
            const response = await getLitigationCaseTypes(); 

            const payload = response?.data || {};
            const rows = Array.isArray(payload?.data?.results)
                ? payload.data.results
                : Array.isArray(payload?.results)
                    ? payload.results
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];

            if (rows.length > 0) {
                const allReliefs = rows
                    .map((item) => item?.relief_sought || item?.batch?.relief_sought)
                    .filter(Boolean);

                const uniqueReliefs = [...new Set(allReliefs)];

                const options = uniqueReliefs.map((slug) => ({
                    label: formatCaseTypeLabel(slug),
                    value: slug,
                }));

                setCaseTypeOptions(options);
            } else {
                setCaseTypeOptions([]);
                console.error("Failed to fetch case types:", response?.message || "Check implementation of getLitigationCaseTypes.");
            }
        } catch (error) {
            console.error("Error fetching case types:", error);
        }
    };
    
    // --- API Fetch Functions (Client/Branch - kept for completeness) ---
    const fetchClients = async () => {
        try {
            const response = await getLitigationClients();
            const payload = response?.data || {};
            const rows = Array.isArray(payload?.data?.results)
                ? payload.data.results
                : Array.isArray(payload?.results)
                    ? payload.results
                    : [];

            if (rows.length > 0) {
                const options = rows.map((client) => ({
                    label: client.full_name || client.name || String(client.id),
                    value: client.id,
                }));
                setClientOptions(options);
            } else {
                setClientOptions([]);
            }
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };

    /**
     * Fetches branches, updates the full list, and sets up initial search states.
     */
    const fetchBranches = async (clientId) => {
        try {
            const response = await getLitigationBranches(clientId);
            
            const payload = response?.data || {};
            const rows = Array.isArray(payload?.data?.results)
                ? payload.data.results
                : Array.isArray(payload?.results)
                    ? payload.results
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];

            if (rows.length > 0) {
                const options = rows.map((branch) => ({
                    label: branch.branch_name || branch.name || String(branch.id),
                    value: branch.id,
                }));
                // Store the full list
                setBranchOptions(options); 
            } else {
                setBranchOptions([]);
            }
            // Clear search states regardless of success/failure
            setBranchSearch("");
            setFilteredBranchOptions([]);
        } catch (error) {
            console.error("Error fetching branches:", error);
            setBranchOptions([]);
            setBranchSearch("");
            setFilteredBranchOptions([]);
        }
    };
    // --- End API Fetch Functions ---


    // --- Effect Hooks for API Calls ---
    useEffect(() => {
        // Fetch clients and case types on component mount
        fetchClients();
        fetchCaseTypes();
    }, []);

    useEffect(() => {
        // Fetch branches when selectedClient changes
        if (selectedClient) {
            fetchBranches(selectedClient);
        } else {
            setBranchOptions([]);
            setSelectedBranch(""); 
            setBranchSearch("");
            setFilteredBranchOptions([]);
        }
    }, [selectedClient]);
    
    // Set initial branch search text based on selectedBranch ID if an ID exists
    useEffect(() => {
        if (selectedBranch) {
            if (selectedBranch === "") {
                // If selectedBranch is "", it means "All Branches" was selected.
                setBranchSearch("All Branches");
            } else {
                // Find the label for the selected ID
                const selected = branchOptions.find(b => b.value === selectedBranch);
                if (selected) {
                    setBranchSearch(selected.label);
                } else {
                    // If ID exists but no label found (e.g., branch list hasn't loaded yet), clear search.
                    setBranchSearch("");
                }
            }
        } else {
            setBranchSearch("");
        }
    }, [selectedBranch, branchOptions]); 
    // --- End Effect Hooks ---

    
    // --- New Branch Search Handlers ---

    /**
     * Handles typing in the branch search input.
     * Implements the 3-character minimum filter activation.
     */
    const handleBranchSearchChange = (searchValue) => {
        setBranchSearch(searchValue);
        const lowerCaseSearch = searchValue.toLowerCase();
        
        // 1. Clear selected branch if current search doesn't match the selection
        // We only clear the selection if the current selectedBranch is NOT "All Branches" ("")
        if (selectedBranch && selectedBranch !== "") {
            const selected = branchOptions.find(b => b.value === selectedBranch);
            
            // Check if the currently selected branch's label starts to mismatch the search input
            if (selected && !selected.label.toLowerCase().includes(lowerCaseSearch)) {
                setSelectedBranch(""); // Deselect the specific branch
            }
        }
        
        // Always include 'All Branches' as the first option
        const allBranchesOption = { label: "All Branches", value: "" };
        let filteredResults = [];

        // 2. Filter logic (3 character minimum)
        if (searchValue.trim().length >= 3) {
            filteredResults = branchOptions.filter(branch =>
                branch.label.toLowerCase().includes(lowerCaseSearch)
            );
            
            // Set filtered results, ensuring 'All Branches' is at the start
            setFilteredBranchOptions([allBranchesOption, ...filteredResults]); 
        } else {
            // If less than 3 chars, only show 'All Branches' if the input is empty or matches 'all'
            if (searchValue.trim() === "" || lowerCaseSearch === "all") {
                setFilteredBranchOptions([allBranchesOption]);
            } else {
                setFilteredBranchOptions([]);
            }
        }
    };

    /**
     * Handles selecting an option from the filtered branch list.
     */
    const handleBranchSelect = (selectedOption) => {
        setSelectedBranch(selectedOption.value);
        setBranchSearch(selectedOption.label);
        setFilteredBranchOptions([]);
    };
    // --- End New Branch Search Handlers ---


    const closeFilter = () => {
        close();
    };

    const applyFilters = () => {

        const newFilters = {
            // Clear general search fields
            search: "",
            batch_no: "",
            date_from: null,
            date_to: null,
            specific_date: null,
            batch_status: "",
            all_communication_mode: "",
            
            // Advanced filters
            borrower_name: borrowerName,
            client: selectedClient,
            branch: selectedBranch, // <--- This holds the Branch ID or "" (for All Branches)
            // branchName: finalBranchName, // <-- REMOVED THIS FIELD
            relief_sought: selectedCaseType, 
            
            document_type: selectedDocumentType,
            
            case_status: selectedFilingStatus, 
            email_reply_status: emailReplyStatus,
            case_data_status: caseDataStatus,
        };

        setPrevFilter(newFilters);
        close();
    };

    return (
        <Modal size="md" show={show} onHide={close} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Advanced Filters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Borrower Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search by Borrower Name (Customer Name)"
                            value={borrowerName}
                            onChange={(e) => setBorrowerName(e.target.value)}
                        />
                    </Form.Group>

                    
                    <Form.Group className="mb-3">
                        <Form.Label>Client Name</Form.Label>
                        <Form.Select 
                            value={selectedClient}
                            onChange={(e) => {
                                setSelectedClient(e.target.value);
                                // Branch clearing handled in useEffect
                            }}
                        >
                            <option value="">All Clients</option>
                            {clientOptions.map((client) => (
                                <option key={client.value} value={client.value}>
                                    {client.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    
                    <Form.Group className="mb-3 position-relative">
                        <Form.Label>Branch</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={!selectedClient ? "Select client first" : "Type Branch Name (min 3 chars)"}
                            value={branchSearch}
                            onChange={(e) => handleBranchSearchChange(e.target.value)}
                            disabled={!selectedClient} 
                            autoComplete="off"
                        />
                        {/* Display filtered results */}
                        {(branchSearch.length >= 3 || (filteredBranchOptions.length > 0 && filteredBranchOptions[0].value === "")) && (
                            <ul className="list-group position-absolute w-100 z-3 shadow" 
                                style={{ maxHeight: '200px', overflowY: 'auto', top: '100%', left: 0 }}>
                                
                                {filteredBranchOptions.map((branch) => (
                                    <li
                                        key={branch.value || 'all'}
                                        // Highlight if this is the currently applied filter
                                        className={`list-group-item list-group-item-action ${branch.value === selectedBranch ? 'active' : ''}`}
                                        onClick={() => handleBranchSelect(branch)} 
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {branch.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* Show an alert if 3+ chars typed but only 'All Branches' is in the results */}
                        {branchSearch.length >= 3 && filteredBranchOptions.length === 1 && filteredBranchOptions[0].value === "" && (
                            <div className="alert alert-warning mt-2 p-1 text-center" role="alert" style={{ fontSize: '0.8rem' }}>
                                No specific branches found for "{branchSearch}".
                            </div>
                        )}
                    </Form.Group>

                    
                    <Form.Group className="mb-3">
                        <Form.Label>Type of Case</Form.Label>
                        <Form.Select 
                            value={selectedCaseType}
                            onChange={(e) => setSelectedCaseType(e.target.value)}
                        >
                            <option value="">All Case Types</option>
                            {caseTypeOptions.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    
                    {/* Document Type dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label>Document Type</Form.Label>
                        <Form.Select 
                            value={selectedDocumentType}
                            onChange={(e) => setSelectedDocumentType(e.target.value)}
                        >
                            <option value="">All Document Types</option>
                            {documentTypeOptions.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    
                    {/* Filing Status dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label>Filing Status</Form.Label>
                        <Form.Select 
                            value={selectedFilingStatus}
                            onChange={(e) => setSelectedFilingStatus(e.target.value)}
                        >
                            <option value="">-- Select Status --</option> 
                            {filingStatusOptions.map((status) => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    
                    <Form.Group className="mb-3">
                        <Form.Label>Email Reply Status</Form.Label>
                        <Form.Select 
                            value={emailReplyStatus}
                            onChange={(e) => setEmailReplyStatus(e.target.value)}
                        >
                            <option value="">-- Select Reply Status --</option>
                            <option value="replied">Replied</option>
                            <option value="not_replied">Not Replied</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Case Data Status dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label>Case Data</Form.Label>
                        <Form.Select 
                            value={caseDataStatus}
                            onChange={(e) => setCaseDataStatus(e.target.value)}
                        >
                            <option value="">-- Select Case Data Status --</option>
                            {caseDataStatusOptions.map((status) => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeFilter}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={applyFilters}>
                    Apply Filters
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default CaseListingFilter;